/**
 * Official public holidays of Iran, for the Jalali date picker.
 *
 * Solar holidays are fixed by law (لایحه قانونی تعیین تعطیلات رسمی کشور) and hold every year;
 * the law closes 29 Esfand through 4 Farvardin, so 30 Esfand of a leap year is a holiday too.
 *
 * Lunar (Hijri) holidays move every year, so they are taken from each year's official calendar
 * (تقویم رسمی کشور, Calendar Center of the Institute of Geophysics, University of Tehran,
 * calendar.ut.ac.ir), transcribed on 2026-09-28 and checked against the official number of
 * holiday days: 1402 and 1403 have 27, 1404 and 1405 have 26 (three of 1405's fall on a Friday).
 * Add a year when the Calendar Center publishes it; until then the picker shows only its solar
 * holidays and says so.
 */

const SOLAR_HOLIDAYS: Record<string, string> = {
  '01/01': 'آغاز نوروز',
  '01/02': 'عید نوروز',
  '01/03': 'عید نوروز',
  '01/04': 'عید نوروز',
  '01/12': 'روز جمهوری اسلامی ایران',
  '01/13': 'روز طبیعت',
  '03/14': 'رحلت امام خمینی (ره)',
  '03/15': 'قیام ۱۵ خرداد',
  '11/22': 'پیروزی انقلاب اسلامی',
  '12/29': 'روز ملی شدن صنعت نفت',
  // Only a leap year has a 30 Esfand.
  '12/30': 'تعطیلات نوروز',
}

const IMAM_ALI_MARTYRDOM = 'شهادت حضرت علی (ع)'
const EID_FITR = 'عید سعید فطر'
const EID_FITR_HOLIDAY = 'تعطیل به مناسبت عید سعید فطر'
const IMAM_SADEGH = 'شهادت امام جعفر صادق (ع)'
const EID_ADHA = 'عید سعید قربان'
const EID_GHADIR = 'عید سعید غدیر خم'
const TASUA = 'تاسوعای حسینی'
const ASHURA = 'عاشورای حسینی'
const ARBAEEN = 'اربعین حسینی'
const PROPHET_DEMISE = 'رحلت رسول اکرم (ص) و شهادت امام حسن مجتبی (ع)'
const IMAM_REZA = 'شهادت امام رضا (ع)'
const IMAM_ASKARI = 'شهادت امام حسن عسکری (ع)'
const PROPHET_BIRTH = 'ولادت رسول اکرم (ص) و امام جعفر صادق (ع)'
const FATIMA = 'شهادت حضرت فاطمه زهرا (س)'
const IMAM_ALI_BIRTH = 'ولادت امام علی (ع) و روز پدر'
const MABAS = 'مبعث رسول اکرم (ص)'
const MAHDI_BIRTH = 'ولادت حضرت قائم (عج)'

const LUNAR_HOLIDAYS: Record<number, Record<string, string>> = {
  1402: {
    '01/23': IMAM_ALI_MARTYRDOM,
    '02/02': EID_FITR,
    '02/03': EID_FITR_HOLIDAY,
    '02/26': IMAM_SADEGH,
    '04/08': EID_ADHA,
    '04/16': EID_GHADIR,
    '05/05': TASUA,
    '05/06': ASHURA,
    '06/15': ARBAEEN,
    '06/23': PROPHET_DEMISE,
    '06/25': IMAM_REZA,
    '07/02': IMAM_ASKARI,
    '07/11': PROPHET_BIRTH,
    '09/26': FATIMA,
    '11/05': IMAM_ALI_BIRTH,
    '11/19': MABAS,
    '12/06': MAHDI_BIRTH,
  },
  1403: {
    '01/13': IMAM_ALI_MARTYRDOM,
    '01/22': EID_FITR,
    '01/23': EID_FITR_HOLIDAY,
    '02/15': IMAM_SADEGH,
    '03/28': EID_ADHA,
    '04/05': EID_GHADIR,
    '04/25': TASUA,
    '04/26': ASHURA,
    '06/04': ARBAEEN,
    '06/12': PROPHET_DEMISE,
    '06/14': IMAM_REZA,
    '06/22': IMAM_ASKARI,
    '06/31': PROPHET_BIRTH,
    '09/15': FATIMA,
    '10/25': IMAM_ALI_BIRTH,
    '11/09': MABAS,
    '11/26': MAHDI_BIRTH,
  },
  1404: {
    '01/02': IMAM_ALI_MARTYRDOM,
    '01/11': EID_FITR,
    '01/12': EID_FITR_HOLIDAY,
    '02/04': IMAM_SADEGH,
    '03/16': EID_ADHA,
    '03/24': EID_GHADIR,
    '04/14': TASUA,
    '04/15': ASHURA,
    '05/23': ARBAEEN,
    '05/31': PROPHET_DEMISE,
    '06/02': IMAM_REZA,
    '06/10': IMAM_ASKARI,
    '06/19': PROPHET_BIRTH,
    '09/03': FATIMA,
    '10/13': IMAM_ALI_BIRTH,
    '10/27': MABAS,
    '11/15': MAHDI_BIRTH,
    '12/20': IMAM_ALI_MARTYRDOM,
  },
  1405: {
    '01/01': EID_FITR,
    '01/02': EID_FITR_HOLIDAY,
    '01/25': IMAM_SADEGH,
    '03/06': EID_ADHA,
    '03/14': EID_GHADIR,
    '04/03': TASUA,
    '04/04': ASHURA,
    '05/13': ARBAEEN,
    '05/21': PROPHET_DEMISE,
    '05/22': IMAM_REZA,
    '05/30': IMAM_ASKARI,
    '06/08': PROPHET_BIRTH,
    '08/22': FATIMA,
    '10/02': IMAM_ALI_BIRTH,
    '10/16': MABAS,
    '11/04': MAHDI_BIRTH,
    '12/09': IMAM_ALI_MARTYRDOM,
    '12/19': EID_FITR,
    '12/20': EID_FITR_HOLIDAY,
  },
}

const dayOfYearKey = (month: number, day: number) =>
  `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`

/** The official occasions that close the given Jalali day; empty when it is a working day. */
export const holidayNamesOn = (year: number, month: number, day: number): string[] => {
  const key = dayOfYearKey(month, day)
  return [SOLAR_HOLIDAYS[key], LUNAR_HOLIDAYS[year]?.[key]].filter((name): name is string =>
    Boolean(name)
  )
}

/** Whether the official lunar holidays of a Jalali year are known to the picker. */
export const hasLunarHolidays = (year: number) => Boolean(LUNAR_HOLIDAYS[year])

/** The holidays of one Jalali month, in day order. */
export const holidaysInMonth = (year: number, month: number, daysInMonth: number) => {
  const holidays: {day: number; names: string[]}[] = []
  for (let day = 1; day <= daysInMonth; day += 1) {
    const names = holidayNamesOn(year, month, day)
    if (names.length) holidays.push({day, names})
  }
  return holidays
}
